<template>
  <router-link
    :class="[
      $style.calendarSummaryItem,
      {
        [$style.calendarSummaryItem__noStaff]:
          calendarSummary.assignedStaffs.length === 0,
      },
    ]"
    :to="{
      name: 'calendarSettingDetail',
      params: {
        entranceGUID: route.params.entranceGUID,
        calendarGUID: calendarSummary.guid,
      },
    }"
  >
    <div :class="$style.calendarSummaryItem__status">
      <StateChip
        :text="statusString(calendarSummary.status)"
        :variation="statusVariation(calendarSummary.status)"
      />
    </div>
    <div :class="[$style.calendarSummaryItem__content, $style.content]">
      <p :class="$style.content__publicTitle">
        {{ calendarSummary.publicTitle }}
      </p>
      <p
        v-if="calendarSummary.privateTitle"
        :class="$style.content__privateTitle"
      >
        {{ calendarSummary.privateTitle }}
      </p>
      <p
        :class="$style.content__description"
        v-if="calendarSummary.description"
      >
        {{ calendarSummary.description.slice(0, 90) }}
      </p>

      <div
        v-if="calendarSummary.assignedStaffs.length !== 0"
        :class="$style.content__assignedStaffs"
      >
        <div
          v-for="staff in getSortedStaffs(calendarSummary.assignedStaffs)"
          :key="staff.guid"
          :class="[
            {
              [$style['content__assignedStaffs--linked']]:
                linkedStaffGuids.includes(staff.guid),
              [$style['content__assignedStaffs--unlinked']]:
                !linkedStaffGuids.includes(staff.guid),
            },
          ]"
        >
          <IconPerson />
          {{ staff.displayName }}
          <template v-if="!linkedStaffGuids.includes(staff.guid)">
            (アカウント再連携が必要)
          </template>
        </div>
      </div>
      <div
        v-else-if="
          calendarSummary.assignedStaffs.length === 0 &&
          calendarSummary.status === CalendarStatus.Active
        "
        :class="$style.content__noStaffMessage"
      >
        担当者候補がいません。設定するかカレンダーを非公開にすることを強く推奨します。
      </div>
    </div>
  </router-link>
</template>

<script setup lang="ts">
import StateChip from '@/components/atoms/StateChip.vue'
import IconPerson from '@/components/icons/IconPerson.vue'
import { useRoute } from 'vue-router'

import { CalendarStatus } from '@/types/schema.generated.ts'

import type {
  CalendarSummaryItemCalendarAssignedStaffFragment,
  CalendarSummaryItemCalendarSummaryFragment,
} from './CalendarSummaryItem.generated'

import { statusString, statusVariation } from '../calendar'

defineProps<{
  calendarSummary: CalendarSummaryItemCalendarSummaryFragment
  linkedStaffGuids: string[]
}>()

const route = useRoute('calendarSetting')

const getSortedStaffs = (
  staffs: CalendarSummaryItemCalendarAssignedStaffFragment[]
): CalendarSummaryItemCalendarAssignedStaffFragment[] => {
  return staffs.toSorted((a, b) => {
    if (!a.priority || !b.priority) {
      return 0
    }
    if (a.priority && !b.priority) {
      return -1
    }
    if (!a.priority && b.priority) {
      return 1
    }
    return a.priority - b.priority
  })
}
</script>

<style lang="scss" module>
.calendarSummaryItem {
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;

  &:hover {
    text-decoration: none;
    background-color: $color-surface-accent-primary-light;
    border-color: $color-surface-accent-primary-light;
  }

  &__status {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 10%;
  }

  &__content {
    flex: 1;
    padding-left: 8px;
  }

  &__noStaff {
    background-color: $color-caution-accent;

    &:hover {
      background-color: $color-caution-accent;
    }
  }
}

.content {
  &__publicTitle {
    color: $color-okaneko-blue;
    font-weight: bold;
    font-size: 18px;
  }

  &__privateTitle {
    color: $color-text-and-object-medium-emphasis;
    font-size: 14px;
    margin-top: 4px;
  }

  &__description {
    color: $color-text-and-object-high-emphasis;
  }

  &__assignedStaffs {
    margin-top: 8px;
    display: flex;
    flex-flow: row wrap;
    column-gap: 16px;

    &--linked {
      color: $color-text-and-object-medium-emphasis;
    }

    &--unlinked {
      color: $color-text-and-object-accent-red;
    }
  }

  &__noStaffMessage {
    color: $color-text-and-object-high-emphasis-inverse;
    font-weight: bold;
  }
}
</style>
